/**
 * NotificationBell — small header pill showing unread in-app notifications.
 *
 * Feb 2026 — introduced to support the single-hop quotation approval flow:
 * when a quote is approved, the creator and other tier approvers get an
 * FYI note in this stream. Poll interval is a lazy 30s (no websockets;
 * doesn't need to be real-time for approval fan-out).
 *
 * The bell is:
 *  - Hidden until we've had at least one successful count fetch (avoids
 *    the "0" flash on page load).
 *  - Rendered as a fixed top-right chip so it appears on every route
 *    without touching each page component.
 *  - Clickable → opens a lightweight dropdown of the last 20 notifications
 *    with a "Mark all read" affordance and per-item click-to-navigate
 *    for quotation-linked notifications.
 */

import { useEffect, useRef, useState } from 'react';
import { Bell, Check } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useAuthStore from '../store/authStore';
import { useTenantPath } from '../lib/tenantPath';

import { InlineSkeleton } from './Skeletons';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationBell() {
  const { isAuthenticated } = useAuthStore();
  const tp = useTenantPath();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [hydrated, setHydrated] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const wrapRef = useRef(null);

  // Fetch the unread count on a slow interval. Cheap ($count only) so we
  // don't need to be clever about back-off.
  useEffect(() => {
    if (!isAuthenticated) return;
    let mounted = true;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/notifications/unread-count`);
        if (mounted) {
          setUnread(r.data?.count || 0);
          setHydrated(true);
        }
      } catch {
        /* ignore transient errors */
      }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, [isAuthenticated]);

  // When user opens the dropdown, fetch the actual list.
  const openDropdown = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications/mine`);
      // Existing endpoint returns { items, unread_count }.
      setItems(r.data?.items || []);
      if (typeof r.data?.unread_count === 'number') setUnread(r.data.unread_count);
    } catch {
      /* leave empty */
    } finally {
      setLoading(false);
    }
  };

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch {
      /* ignore */
    }
  };

  const handleItemClick = async (n) => {
    // Mark read (fire-and-forget) then navigate if we have a quotation link.
    if (!n.read) {
      axios.post(`${API}/notifications/${n.id}/read`).catch(() => {});
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((c) => Math.max(0, c - 1));
    }
    // Existing helper stores link payload under `data.quotation_id`.
    const qid = n.quotation_id || n.data?.quotation_id;
    if (qid) {
      setOpen(false);
      nav(tp(`/quotations/${qid}`));
    }
  };

  if (!isAuthenticated || !hydrated) return null;

  return (
    <div className="fixed top-4 right-4 z-40" ref={wrapRef}>
      <button
        onClick={() => (open ? setOpen(false) : openDropdown())}
        className="relative w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-md hover:shadow-lg border border-[#D5E8E4] transition text-[#00a69c]"
        title="Notifications"
        data-testid="notification-bell"
      >
        <Bell size={20} weight={unread > 0 ? 'fill' : 'regular'} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-unread-count"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute top-12 right-0 w-96 max-h-[70vh] overflow-y-auto rounded-xl bg-white shadow-2xl border border-[#D5E8E4]"
          data-testid="notification-dropdown"
        >
          <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-4 py-3 border-b border-[#D5E8E4]">
            <h3 className="text-sm font-semibold text-[#052A23]">Notifications</h3>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-[#00a69c] hover:text-[#008f86] flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <Check size={12} weight="bold" /> Mark all read
              </button>
            )}
          </div>
          <div>
            {loading ? (
              <InlineSkeleton />
            ) : items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-[#6D8E87]" data-testid="notification-empty">
                No notifications yet.
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleItemClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-[#F0F5F3] transition ${
                    n.read ? 'bg-white hover:bg-[#F9FBFA]' : 'bg-[#F4F9F7] hover:bg-[#E2F3F0]'
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <p className={`text-sm ${n.read ? 'text-[#3B5A54]' : 'text-[#052A23] font-medium'}`}>
                    {n.title}
                  </p>
                  <p className="text-xs text-[#6D8E87] mt-0.5">{n.message || n.body}</p>
                  <p className="text-[10px] text-[#98B4AE] mt-1">
                    {new Date(n.created_at).toLocaleString()}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
